import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { getCurrentDecimalSeperator, stripFunnyInput } from "@helpers/utils";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";

import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { StakingAmountUIModel } from "@modules/staking/domain/entities/staking-amount-selector-state.model";
import { TradeAmountType } from "@modules/shared/domain/entities/trade-amount-type.model";
import {
	calcExchangeRate,
	calculatePercentage,
	findIndexInAmountModelOptions
} from "./utils/staking-amount-selector.util";
import { PERCENTAGE_OPTIONS } from "@modules/staking/domain/entities/constants";
import { StakingHelperService } from "@modules/staking/domain/+state/utility/staking-helper.service";
import { Helper } from "@helpers/functions";

@Injectable()
export class StakingAmountSelectorFacade {
	public constructor(
		private readonly stakingFacade: StakingFacade,
		private readonly stakingHelperService: StakingHelperService
	) {}

	// Todo: once fiat & tradable rework is done this method needs to use combine latest to merge all data
	public getStakingAmountSelectorState$(): Observable<StakingAmountUIModel> {
		return this.stakingFacade.getStakingProcess$().pipe(
			map((process) => {
				const { stakeable, amount, selectedPercentage, wallet, type } = process;
				const tradable: Tradable | null = this.stakingHelperService.getSelectedTradable(stakeable?.id) || null;

				return {
					options: PERCENTAGE_OPTIONS,
					selectedPercentage,
					stakingType: type,
					value: `${amount}`,
					displayTradable: process.amountType === TradeAmountType.ASSET,
					defaultFiat: this.stakingHelperService.getFiat(),
					tradable,
					walletBalanceInTradable: wallet?.getBalance() || 0,
					stakedAmountInTradable: +stakeable?.stakedAmount || 0,
					hasMultipleWallets: this.getWalletsByStakeableId(tradable?.getId())?.length > 1
				};
			})
		);
	}

	public getWalletsByStakeableId(id: string): TradableWallet[] {
		return this.stakingHelperService.getWalletsByStakeableId(id);
	}

	public triggerExchange(state: StakingAmountUIModel): void {
		let newState: StakingAmountUIModel = {
			...state,
			displayTradable: !state.displayTradable
		};

		let newValue: string = "";

		if (+state.value === 0) {
			newValue = "";
		} else if (newState.selectedPercentage !== -1) {
			newValue = this.applyFormating(
				`${calculatePercentage(newState.options[newState.selectedPercentage], newState)}`
			);
		} else {
			newValue = this.applyFormating(calcExchangeRate(newState));
		}
		this.updateValue(newValue, newState);
	}

	public setPercentage(index: number, state: StakingAmountUIModel): void {
		if (index !== -1) {
			this.updateValue(this.applyFormating(`${calculatePercentage(state.options[index], state)}`), state);
		}
	}

	public updateValue(value: string, state: StakingAmountUIModel): void {
		let strippedValue = value.endsWith(".") ? value.slice(0, -1) : value;

		if (Helper.getLanguage() !== "eng") {
			if (strippedValue.includes(".")) {
				const values = value.split(".");
				strippedValue = values.join("");
			}
		}
		let newValue = stripFunnyInput(strippedValue);
		this.stakingFacade.updateStakingProcess({
			amount: newValue,
			amountType: state.displayTradable ? TradeAmountType.ASSET : TradeAmountType.FIAT,
			selectedPercentage: findIndexInAmountModelOptions(newValue, state),
			error: false
		});
	}

	// TODO: after MVP we need a better solution for this - andré 05.05.2022

	private applyFormating(value: string): string {
		let n = value;
		if (getCurrentDecimalSeperator() === ",") {
			n = n.replace(/\./g, ",");
		}
		return n;
	}
}
